import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

const SETTING_META = {
  prompt_gemini: {
    label: 'Prompt für Gemini (Audio nativ, 1 Call)',
    hint: 'Gemini erhält direkt das Audio. Muss JSON mit visit_title, transcript, documentation liefern.',
    rows: 14,
  },
  prompt_openai: {
    label: 'Prompt für OpenAI-Modelle (GPT-4o, GPT-5, o3 …) – auch Fallback für unbekannte Provider',
    hint: 'OpenAI bekommt nur das Transkript (Stufe 2). Muss JSON mit visit_title, documentation liefern.',
    rows: 14,
  },
  prompt_anthropic: {
    label: 'Prompt für Claude-Modelle (Anthropic)',
    hint: 'Claude bekommt nur das Transkript (Stufe 2). Muss JSON mit visit_title, documentation liefern.',
    rows: 14,
  },
  prompt_moonshot: {
    label: 'Prompt für Moonshot-Modelle (Kimi)',
    hint: 'Optional. Falls leer, greift prompt_openai als Fallback.',
    rows: 14,
  },
  prompt_deepseek: {
    label: 'Prompt für DeepSeek-Modelle',
    hint: 'Optional. Falls leer, greift prompt_openai als Fallback.',
    rows: 14,
  },
  prompt_xai: {
    label: 'Prompt für xAI/Grok-Modelle',
    hint: 'Optional. Falls leer, greift prompt_openai als Fallback.',
    rows: 14,
  },
  prompt_zhipu: {
    label: 'Prompt für Zhipu/GLM-Modelle',
    hint: 'Optional. Falls leer, greift prompt_openai als Fallback.',
    rows: 14,
  },
  transcription_prompt: {
    label: 'Transkriptions-Prompt (Gemini-Flash, Stufe 1 bei externen Modellen)',
    hint: 'Reiner Transkript-Extraktor. Wird von Gemini-Flash für alle nicht-Gemini-Pipelines genutzt.',
    rows: 6,
  },
  default_model: {
    label: 'Default-Modell',
    hint: 'Wird als Voreinstellung im Upload-Tag-Picker verwendet (z.B. "gemini-2.5-pro").',
    rows: 1,
  },
};


export default function AdminSettingsPage() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [status, setStatus] = useState({});
  const [error, setError] = useState(null);

  // Hinzufügen-Dialog
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  // Modelle vom Backend (für Modell-spezifische Prompts)
  const [models, setModels] = useState([]);
  useEffect(() => {
    api.get('/models').then((r) => setModels(r.data.models)).catch(() => {});
  }, []);

  const load = async () => {
    try {
      const res = await api.get('/settings');
      setItems(res.data);
      const map = {};
      res.data.forEach((r) => { map[r.key] = r.value; });
      setDrafts(map);
    } catch (e) {
      setError(e.response?.data?.error || 'Laden fehlgeschlagen');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    setSaving((s) => ({ ...s, [key]: true }));
    setStatus((s) => ({ ...s, [key]: null }));
    try {
      await api.put(`/settings/${key}`, { value });
      setStatus((s) => ({ ...s, [key]: 'ok' }));
      await load();
      return true;
    } catch (e) {
      setStatus((s) => ({ ...s, [key]: e.response?.data?.error || 'Speichern fehlgeschlagen' }));
      return false;
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  // Vorschläge für neue Keys: Modelle ohne eigenen prompt_<modelname>
  const existingKeys = useMemo(() => new Set(items.map((i) => i.key)), [items]);
  const suggestedKeys = useMemo(() => {
    const out = [];
    for (const m of models) {
      const k = `prompt_${m.id}`;
      if (!existingKeys.has(k)) out.push({ key: k, label: `Override für ${m.label} (${m.id})` });
    }
    return out;
  }, [models, existingKeys]);

  const onAdd = async () => {
    const k = newKey.trim();
    if (!k) return;
    const ok = await save(k, newValue);
    if (ok) {
      setAdding(false);
      setNewKey('');
      setNewValue('');
    }
  };

  return (
    <div className="admin">
      <Link to="/">← Zurück</Link>
      <h1>Einstellungen</h1>
      {error && <div className="error">{error}</div>}

      {items.map((item) => {
        const meta = SETTING_META[item.key] || {
          label: item.key,
          hint: item.key.startsWith('prompt_')
            ? 'Modellspezifischer Prompt (Override). Hat Vorrang vor providerweitem Prompt.'
            : '',
          rows: 14,
        };
        const changed = drafts[item.key] !== item.value;
        return (
          <div key={item.key} className="setting-block">
            <label>
              <div className="setting-label">{meta.label}</div>
              {meta.hint && <div className="meta">{meta.hint}</div>}
              <textarea
                value={drafts[item.key] ?? ''}
                onChange={(e) => setDrafts({ ...drafts, [item.key]: e.target.value })}
                rows={meta.rows}
              />
            </label>
            <div className="setting-actions">
              <span className="meta">
                Zuletzt geändert: {new Date(item.updated_at).toLocaleString('de-DE')}
              </span>
              <div>
                {status[item.key] === 'ok' && <span className="saved-ok">✓ gespeichert</span>}
                {status[item.key] && status[item.key] !== 'ok' && (
                  <span className="failed">{status[item.key]}</span>
                )}
                <button
                  onClick={() => save(item.key, drafts[item.key])}
                  disabled={!changed || saving[item.key]}
                >
                  {saving[item.key] ? '…' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {items.length === 0 && !error && <p className="meta">Keine Einstellungen.</p>}

      {/* Add-Setting-Bereich */}
      {!adding ? (
        <button onClick={() => setAdding(true)} className="btn-primary-sm">
          + Neue Einstellung / Modell-Override
        </button>
      ) : (
        <div className="setting-block">
          <div className="setting-label">Neue Einstellung anlegen</div>
          <div className="meta">
            Frei: <code>prompt_kimi-k2.6</code>, <code>prompt_moonshot</code>, …
            <br />Vorschläge: Modell-Overrides für die in LiteLLM freigeschalteten Modelle.
          </div>
          <div className="add-setting-row">
            <input
              list="setting-key-suggestions"
              type="text"
              placeholder="z.B. prompt_kimi-k2.6"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              autoFocus
            />
            <datalist id="setting-key-suggestions">
              {suggestedKeys.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </datalist>
          </div>
          <textarea
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            rows={10}
            placeholder="Prompt-Text …"
          />
          <div className="setting-actions">
            <span className="meta">
              Modell-Overrides haben Vorrang vor providerweiten Prompts.
            </span>
            <div>
              {status[newKey] && status[newKey] !== 'ok' && (
                <span className="failed">{status[newKey]}</span>
              )}
              <button onClick={onAdd} disabled={!newKey.trim() || saving[newKey]} className="btn-primary-sm">
                {saving[newKey] ? '…' : 'Anlegen'}
              </button>
              <button onClick={() => { setAdding(false); setNewKey(''); setNewValue(''); }} className="btn-cancel">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
